/**
 * Esquema de desmame da receita (corticoide pós-op, uveíte…): uma lista de
 * etapas `{ posologia, duracao }` presa ao medicamento. Aqui fica a lógica
 * pura — limpar o que veio do editor/registro, sugerir a escada padrão a
 * partir da posologia e rotular as etapas pra tela e pro papel
 * ("1ª semana", "8º ao 10º dia", "Depois").
 *
 * O registro salvo usa a MESMA chave (`desmame`) e o mesmo formato do
 * rascunho — sem tradução pt/en, pra não sumir em nenhuma conversão.
 */
import type { AIDesmameEtapa, AIMedication } from '@/types/ai';

const DURACAO_PADRAO = '7 dias';
export const DURACAO_COM_DESMAME = 'conforme esquema de desmame';

/**
 * Normaliza o que vier (editor, registro salvo, modelo antigo) numa lista de
 * etapas com strings aparadas. Etapa sem posologia nem duração sai.
 */
export function cleanDesmame(raw: unknown): AIDesmameEtapa[] {
  if (!Array.isArray(raw)) return [];
  const out: AIDesmameEtapa[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const posologia = typeof r.posologia === 'string' ? r.posologia.trim() : '';
    const duracao = typeof r.duracao === 'string' ? r.duracao.trim() : '';
    if (!posologia && !duracao) continue;
    out.push({ posologia, duracao });
  }
  return out;
}

/**
 * Pedaço pra espalhar no objeto gravado (`...desmameField(m)`) — some quando
 * não há etapa, pra nunca gravar `desmame: []` nem `undefined` no Firestore.
 */
export function desmameField(m: Pick<AIMedication, 'desmame'>): { desmame?: AIDesmameEtapa[] } {
  const etapas = cleanDesmame(m.desmame);
  return etapas.length > 0 ? { desmame: etapas } : {};
}

// ── Duração ────────────────────────────────────────────────────────────

const NUMEROS_POR_EXTENSO: Record<string, number> = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6,
  sete: 7, oito: 8, nove: 9, dez: 10, quinze: 15,
};

function semAcento(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Dias de uma duração escrita à mão: "7 dias", "07 dias", "por 10 dias",
 * "1 semana", "duas semanas". Devolve null quando não dá pra saber
 * (ex.: "até o retorno", "1 mês") — aí a etapa sai com "por …" no papel.
 */
export function parseDuracaoDias(duracao: string): number | null {
  const s = semAcento(duracao).trim().replace(/^por\s+/, '');
  const m = s.match(/^(\d+|[a-z]+)\s*(dias?|d|semanas?|sem)\.?$/);
  if (!m) return null;
  const n = /^\d+$/.test(m[1]) ? Number(m[1]) : NUMEROS_POR_EXTENSO[m[1]];
  if (!n || n <= 0) return null;
  return m[2].startsWith('s') ? n * 7 : n;
}

// ── Rótulos ────────────────────────────────────────────────────────────

export interface DesmameLinha {
  /** "1ª semana", "8º ao 10º dia", "Depois" — vazio quando não dá pra contar. */
  rotulo: string;
  /** Posologia da etapa, com "por X" quando a etapa não tem rótulo de prazo. */
  texto: string;
}

/**
 * Rotula as etapas contando os dias desde o início: blocos de 7 dias
 * alinhados viram "Nª semana"; os demais, "Xº ao Yº dia". Se uma etapa tem
 * duração que não dá pra contar, dali em diante sai só "por <duração>".
 * Etapa final sem duração (ex.: "Suspender") vira "Depois".
 */
export function desmameLinhas(etapas: unknown): DesmameLinha[] {
  return rotularEtapas(cleanDesmame(etapas));
}

/**
 * Mesmo rótulo de `desmameLinhas`, mas SEM descartar etapa vazia — o editor
 * precisa de um rótulo por linha de input, inclusive a que está em branco.
 */
export function rotularEtapas(etapas: AIDesmameEtapa[]): DesmameLinha[] {
  const limpas = etapas.map((e) => ({ posologia: e.posologia.trim(), duracao: e.duracao.trim() }));
  let decorridos: number | null = 0;
  return limpas.map((e, i) => {
    const ultima = i === limpas.length - 1;
    if (!e.duracao) {
      // Etapa do meio sem prazo: dali em diante não dá mais pra contar dias.
      if (!ultima) decorridos = null;
      return { rotulo: ultima && i > 0 ? 'Depois' : '', texto: e.posologia };
    }
    const dias = parseDuracaoDias(e.duracao);
    if (dias == null || decorridos == null) {
      decorridos = null;
      const prazo = e.duracao.replace(/^por\s+/i, '');
      return { rotulo: '', texto: e.posologia ? `${e.posologia} — por ${prazo}` : `por ${prazo}` };
    }
    const inicio = decorridos + 1;
    const fim = decorridos + dias;
    decorridos = fim;
    const rotulo =
      dias === 7 && (inicio - 1) % 7 === 0 ? `${(inicio - 1) / 7 + 1}ª semana`
      : dias === 1 ? `${inicio}º dia`
      : `${inicio}º ao ${fim}º dia`;
    return { rotulo, texto: e.posologia };
  });
}

/** Resumo curto pra cartões/listas: "5 etapas · 35 dias". */
export function desmameResumo(etapas: AIDesmameEtapa[] | undefined): string {
  const limpas = cleanDesmame(etapas);
  if (limpas.length === 0) return '';
  const comPrazo = limpas.filter((e) => e.duracao);
  const dias = comPrazo.map((e) => parseDuracaoDias(e.duracao));
  const total = dias.every((d) => d != null) ? dias.reduce<number>((a, d) => a + (d ?? 0), 0) : null;
  const etapasTxt = `${comPrazo.length || limpas.length} etapa${(comPrazo.length || limpas.length) === 1 ? '' : 's'}`;
  return total ? `${etapasTxt} · ${total} dias` : etapasTxt;
}

// ── Sugestão (escada padrão) ───────────────────────────────────────────

/**
 * Intervalos da escada, em horas. 24 = 1 vez ao dia; 48 = dias alternados
 * (só entra pelo "+ Etapa" — a sugestão padrão termina em 1 vez ao dia).
 */
const ESCADA_HORAS = [1, 2, 4, 6, 8, 12, 24, 48] as const;

/** Intervalo (em horas) que a posologia descreve, ou null. */
export function intervaloHoras(posologia: string): number | null {
  const s = semAcento(posologia);
  let m = s.match(/a cada\s+(\d+)\s*(?:h\b|hs?\b|horas?)/);
  if (m) return Number(m[1]);
  if (/a cada hora|de hora em hora/.test(s)) return 1;
  if (/dias alternados|dia sim,? dia nao/.test(s)) return 48;
  m = s.match(/de\s+(\d+)\s+em\s+(\d+)\s*(?:h\b|hs?\b|horas?)/);
  if (m && m[1] === m[2]) return Number(m[1]);
  m = s.match(/\b(\d+)\s*\/\s*(\d+)\s*h/);
  if (m && m[1] === m[2]) return Number(m[1]);
  m = s.match(/\b(\d+)\s*(?:x|vezes)\s*(?:ao|por|\/)\s*dia/);
  if (m && Number(m[1]) > 0) return Math.round(24 / Number(m[1]));
  if (/\b(?:uma|1)\s*(?:x|vez)\s*(?:ao|por|\/)\s*dia|\bdose unica diaria\b/.test(s)) return 24;
  return null;
}

/** "1 gota", "2 gotas", "1 comprimido"… da posologia; "Aplicar" pra pomada/gel. */
function doseDaPosologia(posologia: string, forma: string): string {
  const m = posologia.match(
    /(\d+(?:[.,]\d+)?)\s*(gotas?|gts?\b\.?|comprimidos?|cp\b|c[áa]psulas?|aplica[çc](?:ão|ao|ões|oes)|ml\b|mg\b)/i,
  );
  if (m) {
    const n = /^\d+$/.test(m[1]) ? String(Number(m[1])) : m[1]; // "01 gota" → "1 gota"
    const unidade = semAcento(m[2]);
    if (unidade.startsWith('g')) return `${n} ${n === '1' ? 'gota' : 'gotas'}`;
    if (unidade.startsWith('cp') || unidade.startsWith('comprimido')) {
      return `${n} ${n === '1' ? 'comprimido' : 'comprimidos'}`;
    }
    return `${n} ${m[2]}`;
  }
  return /pomada|gel|creme/.test(semAcento(forma)) ? 'Aplicar' : '1 gota';
}

function posologiaDoIntervalo(dose: string, horas: number): string {
  if (horas === 24) return `${dose} 1 vez ao dia`;
  if (horas === 48) return `${dose} em dias alternados`;
  return `${dose} a cada ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
}

function proximoDegrau(horas: number): number | null {
  const prox = ESCADA_HORAS.find((h) => h > horas);
  return prox ?? null;
}

const ETAPA_FINAL: AIDesmameEtapa = { posologia: 'Suspender', duracao: '' };

function ehEtapaFinal(e: AIDesmameEtapa): boolean {
  return !e.duracao.trim() && /^suspend/i.test(e.posologia.trim());
}

/**
 * Escada padrão a partir da posologia atual: começa no intervalo de hoje e
 * vai espaçando (4/4h → 6/6h → 8/8h → 12/12h → 1x/dia), 7 dias cada, e
 * termina em "Suspender". Sem intervalo legível, começa em 4/4h.
 */
export function sugerirDesmame(posologia: string, forma = ''): AIDesmameEtapa[] {
  const dose = doseDaPosologia(posologia, forma);
  const atual = intervaloHoras(posologia);
  let horas: number | null = atual && atual > 0 ? atual : 4;
  const etapas: AIDesmameEtapa[] = [];
  while (horas != null && etapas.length < 8) {
    etapas.push({ posologia: posologiaDoIntervalo(dose, horas), duracao: DURACAO_PADRAO });
    horas = horas >= 24 ? null : proximoDegrau(horas);
  }
  return [...etapas, { ...ETAPA_FINAL }];
}

/** Sugestões pro campo de posologia da etapa, na dose do próprio remédio. */
export function opcoesPosologiaEtapa(posologia: string, forma = ''): string[] {
  const dose = doseDaPosologia(posologia, forma);
  return [
    ...ESCADA_HORAS.map((h) => posologiaDoIntervalo(dose, h)),
    ETAPA_FINAL.posologia,
  ];
}

/**
 * Liga o desmame num medicamento: etapas sugeridas pela posologia e, se a
 * duração estiver vazia, "conforme esquema de desmame".
 */
export function comDesmameSugerido(med: AIMedication): AIMedication {
  return {
    ...med,
    desmame: sugerirDesmame(med.posologia, med.dosagem),
    duracao: med.duracao.trim() ? med.duracao : DURACAO_COM_DESMAME,
  };
}

/** Tira o esquema do medicamento (sem deixar `desmame: undefined` no objeto). */
export function semDesmame(med: AIMedication): AIMedication {
  const { desmame, ...resto } = med;
  void desmame;
  return resto;
}

/**
 * Acrescenta uma etapa no fim (antes do "Suspender", se houver), um degrau
 * mais espaçada que a anterior e com a mesma duração dela.
 */
export function adicionarEtapa(etapas: AIDesmameEtapa[], posologia: string, forma = ''): AIDesmameEtapa[] {
  const temFinal = etapas.length > 0 && ehEtapaFinal(etapas[etapas.length - 1]);
  const corpo = temFinal ? etapas.slice(0, -1) : etapas;
  const anterior = corpo[corpo.length - 1];
  let nova: AIDesmameEtapa;
  if (!anterior) {
    nova = sugerirDesmame(posologia, forma)[0];
  } else {
    const h = intervaloHoras(anterior.posologia);
    const prox = h != null ? proximoDegrau(h) : null;
    nova = {
      posologia:
        h != null && prox != null
          ? posologiaDoIntervalo(doseDaPosologia(anterior.posologia, forma), prox)
          : anterior.posologia,
      duracao: anterior.duracao || DURACAO_PADRAO,
    };
  }
  return temFinal ? [...corpo, nova, etapas[etapas.length - 1]] : [...corpo, nova];
}
